import { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  updateCompanyModuleConfig,
  updateCompanyPageConfig,
  getCompanyModuleConfig,
  getCompanyPageConfig,
  CompanyModuleConfigView,
  ModuleConfigInput,
  PageConfigInput,
  PageConfigView,
  CAPABILITIES_MODULE_ID,
  CAPABILITY_KEYS,
} from '../../../api/Navigation/moduleConfig';
import request from '../../../utils/request';
import { Result } from '../../../models/general';
import {
  navigationModules,
  pageTabGroups,
  mobileModules,
} from '../../../components/layout/navigationModules';
import { ChevronDown, Lock, Smartphone, Monitor, LayoutGrid, PanelTop } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import StickyActionBar from '../../../components/ui/StickyActionBar';
import { Switch } from '../../../components/ui/Switch';
import { InfoTooltip } from '../../../components/ui/Tooltip';

/**
 * Company picker entry. The `modules`/`pages` collections of
 * `CompanyModuleConfigView` are deliberately NOT used here: this page only needs
 * id + name to render the selector, and the per-tenant config is fetched for the
 * selected company alone (CO-135).
 */
type CompanySummary = Pick<CompanyModuleConfigView, 'companyId' | 'companyName'>;

/**
 * Fetch the company list in summary form (Operator only).
 * `?summary=true` is an additive parameter on the existing `navigation/modules/all`
 * route — the server returns the same envelope with empty `modules`/`pages`, so the
 * payload is O(number of companies) instead of O(companies x modules x pages).
 */
async function getCompanySummaries(): Promise<CompanySummary[]> {
  try {
    const response = await request.get<Result<CompanySummary[]>>(
      'navigation/modules/all?summary=true'
    );
    return response.data?.payload ?? [];
  } catch (error) {
    console.error('Failed to fetch company summaries:', error);
    return [];
  }
}

interface ToastState {
  show: boolean;
  message: string;
  type: 'success' | 'error';
}

/**
 * A single toggle row inside an expanded module/section panel.
 * Reused for web pages, sub-module pages, mobile pages, and profile tabs.
 */
interface ConfigRowProps {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: (value: boolean) => void;
  badge?: ReactNode;
  /** Names the screens this row governs. Persistent, because an operator scanning the panel should
   *  not have to hover five rows to learn what the section does. */
  description?: string;
  /** The consequence that the screen names alone do not convey. Short sentence, tooltip-sized. */
  hint?: string;
}

function ConfigRow({
  label, checked, disabled = false, onToggle, badge, description, hint,
}: ConfigRowProps) {
  return (
    <div className="flex items-start justify-between gap-3 px-6 py-3 transition-colors hover:bg-card">
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <span className={`truncate text-sm ${disabled ? 'text-muted-foreground' : 'text-foreground'}`}>
            {label}
          </span>
          {badge}
          {hint && <InfoTooltip content={hint} label={label} />}
        </div>
        {description && (
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
        )}
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onToggle} />
    </div>
  );
}

/**
 * Module Configuration page for Operators.
 * Accordion layout with per-module, per-page and per-TAB toggles across the web
 * app, mobile app, and the pages that own a tab strip. Supports dirty tracking
 * and reset/save via the sticky action bar.
 */
export default function ModuleConfig() {
  const { t } = useTranslation();

  // Data state
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [moduleStates, setModuleStates] = useState<Record<string, boolean>>({});
  const [pageStates, setPageStates] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // UI state
  const [expandedModules, setExpandedModules] = useState<string[]>([]);
  const [expandedTabGroups, setExpandedTabGroups] = useState<string[]>([]);
  const [capabilitiesExpanded, setCapabilitiesExpanded] = useState(false);
  const [expandedMobileModules, setExpandedMobileModules] = useState<string[]>([]);
  const [toast, setToast] = useState<ToastState>({ show: false, message: '', type: 'success' });

  // Initial state snapshots for dirty tracking
  const [initialModuleStates, setInitialModuleStates] = useState<string>('{}');
  const [initialPageStates, setInitialPageStates] = useState<string>('{}');

  // Dirty state
  const isDirty =
    JSON.stringify(moduleStates) !== initialModuleStates ||
    JSON.stringify(pageStates) !== initialPageStates;

  // Toast auto-dismiss
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // Load companies on mount
  useEffect(() => {
    const loadCompanies = async () => {
      setLoading(true);
      const result = await getCompanySummaries();
      setCompanies(result);
      setLoading(false);
    };
    loadCompanies();
  }, []);

  // Build initial states from company data + page config
  const buildStates = useCallback((
    selectedCompany: CompanyModuleConfigView,
    pageConfig: PageConfigView[]
  ) => {
    // Build module states
    const mStates: Record<string, boolean> = {};
    navigationModules.forEach(module => {
      mStates[module.id] = true; // default enabled
    });
    // Seed mobile module states (default all enabled)
    mobileModules.forEach(mobileModule => {
      mStates[mobileModule.id] = true;
    });
    if (selectedCompany.modules && selectedCompany.modules.length > 0) {
      selectedCompany.modules.forEach(mc => {
        mStates[mc.moduleId] = mc.isEnabled;
      });
    }

    // Build page states
    const pStates: Record<string, boolean> = {};
    navigationModules.forEach(module => {
      module.pages.forEach(page => {
        pStates[`${module.id}:${page.path}`] = true; // default all enabled
      });
      // Sub-module pages are keyed by the sub-module id (the runtime nav filter
      // matches on pagePath alone, so the moduleId is purely organizational).
      module.subModules?.forEach(sub => {
        sub.pages.forEach(page => {
          pStates[`${sub.id}:${page.path}`] = true;
        });
      });
    });
    // Seed configurable page-tab keys (default all enabled). Tab config shares
    // the page-config storage: moduleId = tab-group id, pagePath = tab id.
    pageTabGroups.forEach(group => {
      group.tabs.forEach(tab => {
        pStates[`${group.id}:${tab.id}`] = true;
      });
    });
    // Capabilities default to allowed, matching the deny-only contract.
    CAPABILITY_KEYS.forEach(key => {
      pStates[`${CAPABILITIES_MODULE_ID}:${key}`] = true;
    });
    // Seed all mobile module page keys (default all enabled)
    mobileModules.forEach(mobileModule => {
      mobileModule.pages.forEach(page => {
        pStates[`${mobileModule.id}:${page.id}`] = true;
      });
    });
    // Override from API
    pageConfig.forEach(pc => {
      const key = `${pc.moduleId}:${pc.pagePath}`;
      if (key in pStates) {
        pStates[key] = pc.isEnabled;
      }
    });

    return { mStates, pStates };
  }, []);

  // Populate states when company is selected
  useEffect(() => {
    if (selectedCompanyId === null) {
      setModuleStates({});
      setPageStates({});
      setInitialModuleStates('{}');
      setInitialPageStates('{}');
      setExpandedModules([]);
      return;
    }

    // Per-tenant config is fetched for the selected company only — the list
    // endpoint no longer carries it (CO-135), so the cost here is O(1) in tenants.
    const loadCompanyConfig = async () => {
      const [companyConfig, pageConfig] = await Promise.all([
        getCompanyModuleConfig(selectedCompanyId),
        getCompanyPageConfig(selectedCompanyId),
      ]);

      if (!companyConfig) {
        setModuleStates({});
        setPageStates({});
        return;
      }

      const { mStates, pStates } = buildStates(companyConfig, pageConfig);

      setModuleStates(mStates);
      setPageStates(pStates);
      setInitialModuleStates(JSON.stringify(mStates));
      setInitialPageStates(JSON.stringify(pStates));
    };

    loadCompanyConfig();
  }, [selectedCompanyId, buildStates]);

  const handleCompanySelect = (companyId: number | null) => {
    setSelectedCompanyId(companyId);
    setExpandedModules([]);
    setExpandedMobileModules([]);
    setExpandedTabGroups([]);
    setCapabilitiesExpanded(false);
  };

  const handleModuleToggle = (moduleId: string, value: boolean) => {
    setModuleStates(prev => ({ ...prev, [moduleId]: value }));
  };

  const handlePageToggle = (moduleId: string, pagePath: string, value: boolean) => {
    setPageStates(prev => ({ ...prev, [`${moduleId}:${pagePath}`]: value }));
  };

  const toggleExpand = (moduleId: string) => {
    setExpandedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const toggleTabGroupExpanded = (groupId: string) => {
    setExpandedTabGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const toggleMobileModuleExpanded = (moduleId: string) => {
    setExpandedMobileModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleReset = () => {
    setModuleStates(JSON.parse(initialModuleStates) as Record<string, boolean>);
    setPageStates(JSON.parse(initialPageStates) as Record<string, boolean>);
  };

  const handleSave = async () => {
    if (selectedCompanyId === null || !isDirty) return;

    setSaving(true);

    /**
     * Build module config input.
     *
     * 🔴 `displayOrder` IS SENT, and it is not cosmetic. `UpdateCompanyModuleConfig` DELETES every
     * existing row and re-inserts from this payload, so omitting the field wrote NULL over whatever
     * order the tenant had -- meaning a single visit to this screen permanently flattened that
     * company's sidebar ordering. Stamping the source-defined index keeps the sidebar in the order
     * the product intends, and keeps it stable across saves.
     */
    const modules: ModuleConfigInput[] = Object.entries(moduleStates).map(([moduleId, isEnabled]) => {
      /**
       * BOTH registries, because this screen saves both. `moduleStates` is seeded from
       * `navigationModules` AND `mobileModules`, so looking the id up in only the web array left
       * every MOBILE row at NULL -- measured on dev: 14 of 20 rows ordered, 6 still null. The
       * mobile ids are offset past the web ones so the two blocks keep their own internal order
       * and can never interleave.
       */
      const webIndex = navigationModules.findIndex((m) => m.id === moduleId);
      const mobileIndex = mobileModules.findIndex((m) => m.id === moduleId);
      const sourceIndex = webIndex >= 0
        ? webIndex
        : mobileIndex >= 0 ? navigationModules.length + mobileIndex : -1;
      return {
        moduleId,
        isEnabled,
        displayOrder: sourceIndex >= 0 ? sourceIndex : undefined,
      };
    });

    // Build page config input
    const pages: PageConfigInput[] = Object.entries(pageStates).map(([key, isEnabled]) => {
      const [moduleId, ...pathParts] = key.split(':');
      const pagePath = pathParts.join(':'); // rejoin in case path had colons
      return { moduleId, pagePath, isEnabled };
    });

    // Save both in parallel
    const [moduleSuccess, pageSuccess] = await Promise.all([
      updateCompanyModuleConfig(selectedCompanyId, modules),
      updateCompanyPageConfig(selectedCompanyId, pages),
    ]);

    if (moduleSuccess && pageSuccess) {
      // No company-list refetch needed: the list is now id + name only and the
      // save did not change either. The freshly saved states below become the new
      // baseline.
      // Update initial snapshots to reset dirty state
      setInitialModuleStates(JSON.stringify(moduleStates));
      setInitialPageStates(JSON.stringify(pageStates));
      setToast({ show: true, message: t('Configuration saved successfully'), type: 'success' });
    } else {
      setToast({ show: true, message: t('Failed to save configuration'), type: 'error' });
    }

    setSaving(false);
  };

  // Shared badges (depend on `t`, so built here rather than at module scope)
  const lockBadge = (
    <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
      <Lock className="h-3 w-3" />
      {t('Always enabled')}
    </span>
  );
  const comingSoonBadge = (
    <span className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
      {t('Coming Soon')}
    </span>
  );

  if (loading) {
    return (
      <PageLayout
        title={t('Module Configuration')}
        icon={LayoutGrid}
        accentColor="slate"
        maxWidth="5xl"
      >
        <div className="animate-pulse">
          <div className="mb-6 h-10 w-1/2 rounded bg-muted"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 rounded-lg bg-muted"></div>
            ))}
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t('Module Configuration')}
      icon={LayoutGrid}
      accentColor="slate"
      maxWidth="5xl"
    >
      {/* Toast notification */}
      {toast.show && (
        <div
          className={`fixed inset-x-0 bottom-8 z-50 mx-auto w-fit rounded-lg px-6 py-3 shadow-lg ${
            toast.type === 'success'
              ? 'bg-success text-success-foreground'
              : 'bg-destructive text-destructive-foreground'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Company selector */}
      <div className="mb-6">
        <label className="mb-2 block text-sm font-medium text-foreground">
          {t('Select Company')}
        </label>
        <select
          className="w-full max-w-md rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          value={selectedCompanyId ?? ''}
          onChange={(e) => handleCompanySelect(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">{t('Select a company...')}</option>
          {companies.map(c => (
            <option key={c.companyId} value={c.companyId}>{c.companyName}</option>
          ))}
        </select>
      </div>

      {/* Empty state when no companies */}
      {companies.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          <p>{t('No companies found. Only Operators can access this page.')}</p>
        </div>
      )}

      {/* Web App section */}
      {selectedCompanyId !== null && (
        <div className="mb-6">
          <div className="mb-3 flex items-center gap-2">
            <Monitor className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold text-foreground">{t('Web App')}</h2>
          </div>
          <div className="space-y-3">
            {navigationModules.map(module => {
              const isEnabled = moduleStates[module.id] ?? true;
              const isExpanded = expandedModules.includes(module.id);
              const moduleDisabled = !isEnabled;
              const totalPages =
                module.pages.length +
                (module.subModules?.reduce((n, s) => n + s.pages.length, 0) ?? 0);

              return (
                <div
                  key={module.id}
                  className="overflow-hidden rounded-lg border border-border bg-card shadow"
                >
                  {/* Card Header */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    className="flex cursor-pointer select-none items-center justify-between gap-3 px-5 py-4 hover:bg-muted"
                    onClick={() => toggleExpand(module.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleExpand(module.id);
                      }
                    }}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${module.color}`}>
                        <span className="text-sm font-semibold text-white">
                          {module.title.charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground">{t(module.title)}</p>
                        <p className="text-sm text-muted-foreground">
                          {totalPages} {t(totalPages === 1 ? 'page' : 'pages')}
                        </p>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-3">
                      {/* Module toggle (stop propagation so it doesn't expand the card) */}
                      <span
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(v) => handleModuleToggle(module.id, v)}
                        />
                      </span>

                      {/* Expand chevron */}
                      <ChevronDown
                        className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </div>

                  {/* Expanded Page List */}
                  {isExpanded && (
                    <div className="border-t border-border bg-muted">
                      {module.pages.map(page => (
                        <ConfigRow
                          key={`${module.id}:${page.path}`}
                          label={t(page.title)}
                          checked={pageStates[`${module.id}:${page.path}`] ?? true}
                          disabled={moduleDisabled}
                          onToggle={(v) => handlePageToggle(module.id, page.path, v)}
                          badge={page.comingSoon ? comingSoonBadge : undefined}
                        />
                      ))}

                      {module.subModules?.map(sub => (
                        <div key={sub.id}>
                          <div className="px-6 pb-1 pt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {t(sub.title)}
                          </div>
                          {sub.pages.map(page => (
                            <ConfigRow
                              key={`${sub.id}:${page.path}`}
                              label={t(page.title)}
                              checked={pageStates[`${sub.id}:${page.path}`] ?? true}
                              disabled={moduleDisabled}
                              onToggle={(v) => handlePageToggle(sub.id, page.path, v)}
                              badge={page.comingSoon ? comingSoonBadge : undefined}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Mobile App section */}
      {selectedCompanyId !== null && (
        <div className="mb-6">
          <div className="mb-3 flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold text-foreground">{t('Mobile App')}</h2>
          </div>
          <div className="space-y-3">
            {mobileModules.map(mobileModule => {
              const isEnabled = moduleStates[mobileModule.id] ?? true;
              const isExpanded = expandedMobileModules.includes(mobileModule.id);
              const moduleDisabled = !isEnabled;
              const pageCount = mobileModule.pages.length;

              return (
                <div
                  key={mobileModule.id}
                  className="overflow-hidden rounded-lg border border-border bg-card shadow"
                >
                  {/* Card Header */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    className="flex cursor-pointer select-none items-center justify-between gap-3 px-5 py-4 hover:bg-muted"
                    onClick={() => toggleMobileModuleExpanded(mobileModule.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleMobileModuleExpanded(mobileModule.id);
                      }
                    }}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${mobileModule.color}`}>
                        <span className="text-sm font-semibold text-white">
                          {mobileModule.title.charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground">{t(mobileModule.title)}</p>
                        <p className="text-sm text-muted-foreground">
                          {pageCount} {t(pageCount === 1 ? 'page' : 'pages')}
                        </p>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-3">
                      <span
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(v) => handleModuleToggle(mobileModule.id, v)}
                        />
                      </span>
                      <ChevronDown
                        className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </div>

                  {/* Expanded Page List */}
                  {isExpanded && (
                    <div className="border-t border-border bg-muted">
                      {mobileModule.pages.map(page => {
                        const isLocked = page.alwaysEnabled === true;
                        return (
                          <ConfigRow
                            key={page.id}
                            label={t(page.title)}
                            checked={isLocked ? true : (pageStates[`${mobileModule.id}:${page.id}`] ?? true)}
                            disabled={moduleDisabled || isLocked}
                            onToggle={(v) => handlePageToggle(mobileModule.id, page.id, v)}
                            badge={isLocked ? lockBadge : undefined}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Page Tabs section — tabs INSIDE a page, configurable per company.
          Driven entirely by `pageTabGroups`; adding a group there adds a card here. */}
      {selectedCompanyId !== null && (
        <div className="mb-6">
          <div className="mb-3 flex items-center gap-2">
            <PanelTop className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold text-foreground">{t('Page Tabs')}</h2>
          </div>
          <div className="space-y-3">
            {pageTabGroups.map(group => {
              const isExpanded = expandedTabGroups.includes(group.id);
              const enabledCount = group.tabs.filter(
                tab => tab.alwaysEnabled || (pageStates[`${group.id}:${tab.id}`] ?? true)
              ).length;

              return (
                <div
                  key={group.id}
                  className="overflow-hidden rounded-lg border border-border bg-card shadow"
                >
                  {/* Card Header */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    className="flex cursor-pointer select-none items-center justify-between gap-3 px-5 py-4 hover:bg-muted"
                    onClick={() => toggleTabGroupExpanded(group.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleTabGroupExpanded(group.id);
                      }
                    }}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${group.color}`}>
                        <span className="text-sm font-semibold text-white">{group.initial}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground">{t(group.title)}</p>
                        <p className="text-sm text-muted-foreground">
                          <span className="font-mono text-xs">{group.pagePath}</span>
                          {' · '}
                          {enabledCount}/{group.tabs.length} {t(group.tabs.length === 1 ? 'tab' : 'tabs')}
                        </p>
                      </div>
                    </div>
                    <ChevronDown
                      className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </div>

                  {/* Expanded Tab List */}
                  {isExpanded && (
                    <div className="border-t border-border bg-muted">
                      {group.tabs.map(tab => {
                        const isLocked = tab.alwaysEnabled === true;
                        return (
                          <ConfigRow
                            key={tab.id}
                            label={t(tab.title)}
                            checked={isLocked ? true : (pageStates[`${group.id}:${tab.id}`] ?? true)}
                            disabled={isLocked}
                            onToggle={(v) => handlePageToggle(group.id, tab.id, v)}
                            badge={isLocked ? lockBadge : undefined}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Company capabilities section */}
      {selectedCompanyId !== null && (
        <div className="mb-6">
          <div className="overflow-hidden rounded-lg border border-border bg-card shadow">
            {/* Header */}
            <div
              role="button"
              tabIndex={0}
              aria-expanded={capabilitiesExpanded}
              className="flex cursor-pointer select-none items-center justify-between gap-3 px-5 py-4 hover:bg-muted"
              onClick={() => setCapabilitiesExpanded(prev => !prev)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setCapabilitiesExpanded(prev => !prev);
                }
              }}
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-slate-500 to-slate-700">
                  <Lock className="h-4 w-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">
                    {t('moduleConfig.capabilities.title')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {CAPABILITY_KEYS.length} {t('moduleConfig.capabilities.countLabel')}
                  </p>
                </div>
              </div>
              <ChevronDown
                className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                  capabilitiesExpanded ? 'rotate-180' : ''
                }`}
              />
            </div>

            {/* Expanded capability list */}
            {capabilitiesExpanded && (
              <div className="border-t border-border bg-muted">
                <p className="px-6 pb-1 pt-3 text-xs leading-relaxed text-muted-foreground">
                  {t('moduleConfig.capabilities.hint')}
                </p>
                {CAPABILITY_KEYS.map(key => (
                  <ConfigRow
                    key={key}
                    label={t(`moduleConfig.capabilities.${key}`)}
                    description={t(`moduleConfig.capabilities.${key}.screens`)}
                    hint={t(`moduleConfig.capabilities.${key}.effect`)}
                    checked={pageStates[`${CAPABILITIES_MODULE_ID}:${key}`] ?? true}
                    onToggle={(v) => handlePageToggle(CAPABILITIES_MODULE_ID, key, v)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sticky save / reset bar */}
      {selectedCompanyId !== null && (
        <StickyActionBar
          hasUnsavedChanges={isDirty}
          isSaving={saving}
          onReset={handleReset}
          onSave={handleSave}
          t={t}
        />
      )}
    </PageLayout>
  );
}
